/**
 * Every warehouse account of this workspace in one list, for the three places that offer Connect: Settings ›
 * Warehouses, the setup panel's warehouse step (ACT2-1108) and chat's connect card (ACT2-690). All three read this
 * hook, so they cannot list different accounts.
 *
 * - The rows are `buildWarehouseRows`: the connections, the RudderStack accounts of this workspace, and in a sibling
 *   workspace (ACT2-1124) the accounts of the workspaces that Lookout can read (Settings › General), "From
 *   <workspace>".
 * - `connectable` is the Available rows. Connect on an own account connects it (`adoptAsNewConnection`). Connect on an
 *   account of a readable workspace copies it first, then connects the copy, and a Redshift IAM account first asks to
 *   confirm (`CopyAccountDialog`). Both steps end in `onAdoptedAsNew`, so a caller hears of every new connection once.
 */
import type { WarehouseProblem } from '@server/warehouse-failure';
import { useMemo } from 'react';

import { useIsSiblingWorkspace } from '@/_shared/hooks/useSiblingStatus';

import {
  type OrganizationWarehouseAccounts,
  useOrganizationWarehouseAccounts,
} from '../accountCopies/useOrganizationWarehouseAccounts';
import { type AdoptAccountsHook, useAdoptAccounts } from './adopt/useAdoptAccounts';
import { refusalMessage, refusalProblem } from './refusalCopy';
import { useWarehouseAccounts } from './useWarehouseAccounts';
import {
  buildWarehouseRows,
  type ConnectableWarehouseRow,
  connectableWarehouseRows,
  type WarehouseListRow,
} from './warehouseList';

/** One account that Connect can turn into a connection, as the setup step and the connect card show it. */
export interface ConnectableWarehouseAccount {
  id: string;
  name: string;
  typeLabel: string;
  /** Host / database, and "From <workspace>" for an account of a readable workspace. */
  detail: string | null;
  /** The accessible name of its Connect button. */
  connectLabel: string;
  connecting: boolean;
  /** Why its last Connect failed, or null. */
  error: string | null;
  /** The failed Connect's problem, when the server sent one; shown in place of `error`. */
  errorProblem?: WarehouseProblem | undefined;
}

export interface WarehouseAccountList {
  isSiblingWorkspace: boolean;
  connections: ReturnType<typeof useWarehouseAccounts>;
  adopt: AdoptAccountsHook;
  organization: OrganizationWarehouseAccounts;
  /** Every row of Settings › Warehouses. */
  rows: WarehouseListRow[];
  /** The Available rows, as the setup step and the connect card list them. */
  connectable: ConnectableWarehouseAccount[];
  /** How many accounts Lookout does not hold but cannot connect. They are not in `connectable`. */
  unconnectableCount: number;
  /** One of the lists that `connectable` comes from is still loading. */
  isLoading: boolean;
  /** A Connect (or the copy before it) runs now. */
  anyConnecting: boolean;
  /** Starts Connect on the account of `connectable` with this id. */
  connect: (id: string) => void;
  /** A sibling workspace with no readable workspace: the notice points to Settings › General. */
  showReadableWorkspacesNotice: boolean;
}

const CONNECT_FAILED = 'That account could not be connected';

export function useWarehouseAccountList(
  options: {
    /** A connection was saved in the Add warehouse drawer. */
    onConnectionSaved?: () => void;
    /** An account was adopted onto an existing connection. */
    onAdopted?: () => void;
    /** Connect made a new connection from an account (also from the copy of a readable workspace's account). */
    onAdoptedAsNew?: (accountId: string) => void;
  } = {},
): WarehouseAccountList {
  // Null while the sibling status loads: the readable workspaces' accounts may still come, so the list is loading.
  const siblingState = useIsSiblingWorkspace();
  const isSiblingWorkspace = siblingState === true;
  const connections = useWarehouseAccounts({
    ...(options.onConnectionSaved ? { onConnectionSaved: options.onConnectionSaved } : {}),
  });
  const adopt = useAdoptAccounts({
    ...(options.onAdopted ? { onAdopted: options.onAdopted } : {}),
    ...(options.onAdoptedAsNew ? { onAdoptedAsNew: options.onAdoptedAsNew } : {}),
  });
  const organization = useOrganizationWarehouseAccounts({
    enabled: isSiblingWorkspace,
    connectCopy: adopt.adoptAsNewConnection,
    connectingCopyId: adopt.adoptingNewAccountId,
  });
  const rows = useMemo(
    () => buildWarehouseRows(connections.accounts, adopt.accounts, organization.options),
    [connections.accounts, adopt.accounts, organization.options],
  );
  const { connectable: connectableRows, unconnectableCount } = connectableWarehouseRows(rows);

  const adoptFailedFor =
    adopt.adoptAsNewError !== null && adopt.adoptAsNewError !== undefined ? adopt.adoptAsNewErrorAccountId : null;
  const view = (row: ConnectableWarehouseRow): ConnectableWarehouseAccount => {
    if (row.kind === 'rs_account') {
      const failed = adoptFailedFor === row.rsAccount.accountId;
      return {
        id: row.id,
        name: row.name,
        typeLabel: row.typeLabel,
        detail: row.detail,
        connectLabel: `Connect ${row.name}`,
        connecting: adopt.adoptingNewAccountId === row.rsAccount.accountId,
        error: failed ? refusalMessage(adopt.adoptAsNewError, CONNECT_FAILED) : null,
        errorProblem: failed ? refusalProblem(adopt.adoptAsNewError) : undefined,
      };
    }
    return {
      id: row.id,
      name: row.name,
      typeLabel: row.typeLabel,
      detail: row.detail,
      connectLabel: `Connect ${row.name} from ${row.option.workspaceName}`,
      connecting: organization.connectingAccountId === row.option.accountId,
      error: organization.copyError(row.option.accountId),
    };
  };

  return {
    isSiblingWorkspace,
    connections,
    adopt,
    organization,
    rows,
    connectable: connectableRows.map(view),
    unconnectableCount,
    isLoading: siblingState === null || connections.isLoading || adopt.isLoading || organization.isLoading,
    anyConnecting: adopt.adoptingNewAccountId !== null || organization.connectingAccountId !== null,
    connect: (id) => {
      const row = connectableRows.find((candidate) => candidate.id === id);
      if (!row) return;
      adopt.resetAdoptAsNew();
      if (row.kind === 'rs_account') adopt.adoptAsNewConnection({ accountId: row.rsAccount.accountId });
      else organization.connect(row.option);
    },
    showReadableWorkspacesNotice: isSiblingWorkspace && organization.readableWorkspaceCount === 0,
  };
}
